<template>
    <div class="w-full md:mx-auto md:w-10/12">
        <div class="page-title pb-2 flex justify-center md:justify-start">
            <h3 class="ml-0 md:ml-2">{{ formatTextStartCase(type) }} Activity Log</h3>
        </div>

        <div class="bg-white rounded-lg shadow">
            <!-- FILTERS -->
            <div class="px-6 py-3 flex flex-col md:flex-row items-center gap-y-2 sm:gap-y-0 gap-x-0 sm:gap-x-2">
                <!-- Event Filter- Created, Updated, Deleted -->
                <simple-dropdown class="order-0" v-model="currentEventTypeFilter" @input="searchLogs()" :label="currentEventTypeFilter | titlecase">
                    <template slot="buttonOptions">
                        <simple-dropdown-option :value="'All Events'">All Events</simple-dropdown-option>
                        <simple-dropdown-option v-for="(eventType, index) in eventTypes"
                                                :value="eventType"
                                                :key="eventType + index">
                            {{ eventType | titlecase }}
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>

                <!-- Description -->
                <div class="w-full sm:w-7/12 order-2 sm:order-1">
                    <input type="text"
                           v-model="filterText"
                           @keyup="searchLogs()"
                           :placeholder="'Search ' + formatTextStartCase(type) + ' logs'"
                           class="form-control">
                </div>

                <!-- Date Range -->
                <el-date-picker
                    class="w-full order-3 sm:order-2"
                    v-model="dateRangeFilter"
                    value-format="yyyy-MM-dd"
                    type="daterange"
                    unlink-panels
                    start-placeholder="Start date"
                    end-placeholder="End date"
                    :picker-options="datePickerOptions"
                    :clearable="false"
                    @change="searchLogs()">
                </el-date-picker>

                <!-- Users Filter -->
                <div class="w-full sm:w-1/3 order-4 sm:order-3">
                    <el-select v-model="userFilter" multiple collapse-tags placeholder="Select User(s)"
                               class="w-full" @change="searchLogs()">
                        <el-option
                            v-for="user in users"
                            :key="user.id"
                            :label="user.name | capitalize"
                            :value="user.id">
                        </el-option>
                    </el-select>
                </div>

                <!-- Model Filter - All associated models -->
                <simple-dropdown class="order-1 sm:order-4" v-model="currentModelTypeFilter" @input="searchLogs()" :label="currentModelTypeFilter | titlecase">
                    <template slot="buttonOptions">
                        <simple-dropdown-option :value="'All Types'">All Types</simple-dropdown-option>
                        <simple-dropdown-option v-for="(modelType, index) in modelTypes"
                                                :value="modelType"
                                                :key="modelType + index">
                            {{ modelType | titlecase }}
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </div>

            <div class="px-6 pb-6 pt-3">
                <table class="table table-auto" v-if="!loadingLogs && mutableLogs && mutableLogs.length > 0">
                    <thead>
                        <tr>
                            <th class="w-1/3 md:w-1/12 font-semibold p-2">Date</th>
                            <th class="w-1/3 md:w-1/12 font-semibold p-2">User</th>
                            <th class="w-1/3 md:w-1/12 font-semibold p-2">Type</th>
                            <th class="w-1/3 md:w-2/12 font-semibold p-2">Identifier</th>
                            <th class="w-1/3 font-semibold p-2">Description</th>
                        </tr>
                    </thead>

                    <tbody class="">
                        <tr v-for="(log, index) in mutableLogs" class="relative hover:bg-gray-100 hover:font-normal px-2"
                            @click="showAdvancedDetails(log.properties, index)">
                            <td class="px-2 pb-2 md:pb-1">{{ log.created_at | datetime }}</td>
                            <td class="px-2 pb-2 md:pb-1">{{ log.causer_name | capitalize }}</td>
                            <td class="px-2 pb-2 md:pb-1">{{ formatSubjectType(log) | capitalize }}</td>
                            <td class="px-2 pb-2 md:pb-1">{{ (log.subject_name ? log.subject_name : log.parent_name) | capitalize }}</td>
                            <td class="px-2 pb-2 md:pb-1 relative cursor-default" :class="log.description ? styledDescription(log.description) : ''"
                                @mouseover="showAdvancedDetails(log.properties, index)" @mouseleave="showAdvancedDetailsModal = false">
                                {{ log.description }}

                                <!-- If logAttributes is not an empty array, but an object, show drop down table comparing values-->
                                <table v-if="logAttributes.toString() !== '' && showAdvancedDetailsModal && rowNumber === index"
                                       v-click-outside="hideAdvancedDetails"
                                       class="absolute right-0 top-9 z-50 h-full w-full bg-white shadow-lg rounded-lg table">
                                    <thead class="">
                                        <tr>
                                            <th></th>
                                            <th class="font-semibold p-2" v-if="logAttributes.old">Old Values</th>
                                            <th class="font-semibold p-2">{{ log.description && log.description.includes("deleted") ? "Values" : "New Values" }}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="(value, name) in logAttributes.attributes">
                                            <td class="font-semibold px-2 pb-2 md:pb-1"> {{ formatTextStartCase(name) }}</td>
                                            <td class="px-2 pb-2 md:pb-1" :class="logAttributes.old[name] !== value && value === null ? 'text-red-600' : ''"
                                                v-if="logAttributes.old"> {{
                                                    logAttributes.old[name]
                                                }}
                                            </td>
                                            <td class="px-2 pb-2 md:pb-1"
                                                :class="(logAttributes.old && logAttributes.old[name] !== value) ? 'text-green-600' : ''">
                                                {{ value }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div v-else-if="loadingLogs" class="text-gray-500 flex justify-center">
                    <icon-refresh class="h-4 w-4 text-indigo-600 animate-spin"/> &nbsp;Loading Activity Logs...
                </div>

                <div v-else class="text-gray-500 flex justify-center"> No logs to display / No logs with those filters</div>
            </div>
        </div>

        <template v-if="hasFeature('release_plain_paginator_for_activity_log')">
            <plain-pagination
                v-if="mutableLogs && mutableLogs.length > 0"
                :meta="paginatorMeta"
                padding="5"
                @page-changed="searchLogs">
            </plain-pagination>
        </template>

        <template v-else>
            <simple-pagination
                v-if="mutableLogs && mutableLogs.length > 0"
                :meta="paginatorMeta"
                :showItemCounter="false"
                per-page="20"
                padding="5"
                side-navigation-count="5"
                @page-changed="searchLogs">
            </simple-pagination>
        </template>
    </div>
</template>

<script>
import PlainPagination from "../library/PlainPagination.vue";

export default {
    name: "ActivityLog",

    components: {PlainPagination},

    props: {
        id: {
            required: false,
            type: Number,
            description: "The id of the model to fetch logs from, if none is given we fetch all logs",
            default: null,
        },
        type: {
            required: false,
            type: String,
            description: "The type of activity log. E.g. Inspection or MaintenanceRequest",
            default: null,
        },
    },

    data () {
        return {
            loadingLogs: true,
            mutableLogs: [],
            paginatorMeta: null,
            showAdvancedDetailsModal: false,
            logAttributes: {},
            rowNumber: -1,
            filterText: "",
            currentEventTypeFilter: "All Events",
            eventTypes: ["Created", "Updated", "Deleted", "Other"],
            dateRangeFilter: "",
            userFilter: [],
            users: [],
            currentModelTypeFilter: "All Types",
            modelTypes: [
                "Activity",
                "Address",
                "Asset",
                "AssetGroup",
                "ChecklistItem",
                "ChecklistSet",
                "ChecklistSetItem",
                "Contact",
                "Email",
                "Inspection",
                "InspectionArea",
                "InspectionSettings",
                "InspectionSignature",
                "InspectionTemplate",
                "InspectionTemplateArea",
                "Issue",
                "Mailbox",
                "MaintenanceRequest",
                "MaintenanceRequestSettings",
                "Media",
                "Portfolio",
                "QuoteInvoice",
                "Reminder",
                "SavedReply",
                "ServiceProvider",
                "Team",
                "Ticket",
                "TicketPhase",
                "TicketSettings",
                "Todo",
                "User",
            ],
            datePickerOptions: {
                shortcuts: [{
                    text: "Last week",
                    onClick (picker) {
                        const end = new Date();
                        const start = new Date();
                        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                        picker.$emit("pick", [start, end]);
                    }
                }, {
                    text: "Last month",
                    onClick (picker) {
                        const end = new Date();
                        const start = new Date();
                        start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
                        picker.$emit("pick", [start, end]);
                    }
                }, {
                    text: "Last 3 months",
                    onClick (picker) {
                        const end = new Date();
                        const start = new Date();
                        start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
                        picker.$emit("pick", [start, end]);
                    }
                }]
            },
        };
    },

    mounted () {
        if (this.state.currentTeam.subscription_status !== "new") {
            // Set Default date filter to 1 week
            let end = new Date();
            let start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            this.dateRangeFilter = [start, end];
            this.getUsers();
            this.searchLogs();
        }
    },

    methods: {
        styledDescription (description) {
            let created = ["generated", "created", "added", "signed"];
            let updated = ["updated", "changed", "merged", "done"];
            let deleted = ["deleted", "disabled", "removed"];

            if (created.some(word => description.includes(word))) {
                return "text-green-600";
            }
            if (updated.some(word => description.includes(word))) {
                return "text-indigo-600";
            }
            if (deleted.some(word => description.includes(word))) {
                return "text-red-600";
            }
        },

        showAdvancedDetails (attributes, index) {
            this.rowNumber = index;
            this.showAdvancedDetailsModal = true;
            this.logAttributes = attributes;
        },

        hideAdvancedDetails () {
            this.showAdvancedDetailsModal = false;
        },

        formatTextStartCase (name) {
            return name != null ? _.startCase(name) : "";
        },

        formatSubjectType(log){
            return log.subject_type ? _.startCase(log.subject_type.substring(4)) : "";
        },

        getUsers () {
            this.loading = true;
            axios.get("/users", {
                "params": {
                    search_text: "",
                    per_page: 100,
                    status: "all",
                    user_type: "team_users"
                }
            })
            .then(response => {
                this.users = response.data.data;
                this.loading = false;
            }).catch((error) => {
                toastr.error(error.message);
                this.loading = false;
            });
        },

        searchLogs: _.debounce(function (paginationSettings = null) {
            // Extract the page number since we are using the plain pagination component
            let page = 1;
            let perPage = 20;
            if (paginationSettings) {
                if (this.hasFeature("release_plain_paginator_for_activity_log")) {
                    page = new URL(paginationSettings).searchParams.get("page");
                } else {
                    page = paginationSettings && paginationSettings.pageNumber ? paginationSettings.pageNumber : page;
                    perPage = paginationSettings && paginationSettings.perPage ? paginationSettings.perPage : perPage;
                }
            }

            this.loadingLogs = true;
            axios.get("/activity-log/search", {
                params: {
                    id: this.id ? this.id : null,
                    type: this.type ? "App\\" + this.type : null,
                    filter_text: this.filterText,
                    event_type: this.currentEventTypeFilter,
                    //emails are children of threads but the user won't know that, so we change the model if Email is selected
                    model_type: this.currentModelTypeFilter === "Email" ? "Thread" : this.currentModelTypeFilter,
                    date_range: this.dateRangeFilter,
                    user_filter: this.userFilter,
                    page: page,
                    per_page: perPage,
                }
            })
            .then(response => {
                this.mutableLogs = response.data.data;
                this.setPaginatorMeta(response.data);
                this.loadingLogs = false;
            })
            .catch(errors => {
                this.loadingLogs = false;
                toastr.error(errors.message);
            });
        }, 300),

        setPaginatorMeta (requestData) {
            if (this.hasFeature("release_plain_paginator_for_activity_log")) {
                this.paginatorMeta = {
                    prev_page_url: requestData?.links?.prev,
                    first_page_url: requestData?.links?.first,
                    next_page_url: requestData?.links?.next,
                    current_page: requestData?.meta?.current_page,
                };
            } else {
                this.paginatorMeta = requestData?.meta;
            }
        }
    },
};
</script>
